// WO 3 — batched field form. Renders 1+ labelled inputs for the
// founder to fill in a single submit. Replaces the per-field nagging
// where Infi asked occupation, then mobile, then address in three
// separate turns.
//
// Schema: params.fields = [{path, label, ref}, ...]

import { useState } from "react";
import { Loader2 } from "lucide-react";

const HINTS = {
  "contact.email":            { type: "email",  placeholder: "you@email.com" },
  "contact.mobile":           { type: "tel",    placeholder: "10 digits, starts 6-9" },
  "education_occupation.occupation": { type: "text", placeholder: "e.g. Software engineer, Director" },
  "personal.resident_in_india": {
    type: "select",
    options: [{ value: "true", label: "Yes — resident in India" },
              { value: "false", label: "No — not resident in India" }],
  },
};

function hintFor(path) {
  // Strip leading 'stakeholders.' if any
  const p = path.startsWith("stakeholders.") ? path.slice("stakeholders.".length) : path;
  return HINTS[p] || { type: "text", placeholder: "" };
}

function shortLabel(label) {
  // Trim trailing parens like "(yes/no)" so the input label reads cleaner
  return label.replace(/\s*\(yes\/no\)\s*$/i, "").trim();
}

export default function BatchedFieldFormWidget({ subjectRef, fields, busy, onSubmit }) {
  const [form, setForm] = useState(
    () => Object.fromEntries((fields || []).map((f) => [f.path, ""]))
  );

  function update(path, value) {
    setForm((f) => ({ ...f, [path]: value }));
  }

  function canSubmit() {
    return (fields || []).every((f) => {
      const v = form[f.path];
      return v !== undefined && String(v).trim() !== "";
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit() || busy) return;
    onSubmit(form);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
      data-testid="batched-field-form-widget"
    >
      {subjectRef && (
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
          A few details for {subjectRef}
        </div>
      )}
      <div className="space-y-3">
        {(fields || []).map((f) => {
          const h = hintFor(f.path);
          const testid = `field-form-input-${f.path.replace(/\./g, "-")}`;
          return (
            <div key={f.path}>
              <label className="text-[12px] text-slate-700 font-medium block mb-1">
                {shortLabel(f.label)}
              </label>
              {h.type === "select" ? (
                <select
                  data-testid={testid}
                  value={form[f.path] || ""}
                  onChange={(e) => update(f.path, e.target.value)}
                  disabled={busy}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5283F] focus:border-transparent disabled:opacity-50"
                >
                  <option value="" disabled>Select…</option>
                  {h.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid={testid}
                  type={h.type}
                  value={form[f.path] || ""}
                  placeholder={h.placeholder}
                  onChange={(e) => update(f.path, e.target.value)}
                  disabled={busy}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C5283F] focus:border-transparent disabled:opacity-50"
                />
              )}
            </div>
          );
        })}
      </div>
      <button
        type="submit"
        disabled={!canSubmit() || busy}
        data-testid="field-form-submit-btn"
        className="mt-4 w-full bg-[#C5283F] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#A82035] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Saving…
          </>
        ) : (
          <>Save and continue</>
        )}
      </button>
    </form>
  );
}
